import React, { useContext, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import _ from 'lodash';

import { RootState, select } from 'src/configs/store';
import formatOdds from 'src/utils/formatOdds';
import AppModeContext from 'src/containers/AppMode/AppMode.context';
import Icon from 'src/components/Icon/Icon';
import Button from 'src/components/Button/Button';

interface SubmitButtonProps {
  valid: boolean,
  maximum: boolean,
  fixStakes?: () => void
}

function SubmitButton({ valid, maximum, fixStakes }: SubmitButtonProps) {
  const { t } = useTranslation('common');
  const { RETAIL_MODE } = useContext(AppModeContext);

  const { selectedOddsType } = useSelector((state: RootState) => state.odds);
  const { bets, slip, processing, maximumReturn, totalOdds, totalAmount } = useSelector((state: RootState) => state.betslip);
  const noCombinableBets = useSelector(select.betslip.noCombinableBets);
  const totalCombinations = useSelector(select.betslip.totalCombinations);
  const selectedSlip = useSelector(select.betslip.selectedSlip);
  const activeWallet = useSelector((state: RootState) => state.auth.localWalletSelected);

  let message = maximum ? t('betslip.maximumError'): t('betslip.minimumError');
  if (selectedSlip.stakeSelection === 'differentStake') message = (t('betslip.differentStakeError'));

  return (
    <>
      {
        !valid && (
          <div className="betslip-wrapper__minimum-stakes">
            <div className="betslip-wrapper__text">
              <Icon icon="warning" className="betslip-wrapper__warning-icon" />
              { message }
            </div>
            <div
              className="betslip-wrapper__change-stakes"
              onClick={ fixStakes }
            >{ t('betslip.accept') }</div>
          </div>
        )
      }
      {
        valid && (
          noCombinableBets && slip === 1 ? (
            <div className="betslip-wrapper__not-combinable-button">
              <Icon icon="info" />
              <span>{ t('betslip.notCombinableBtn') }</span>
            </div>
          ) : (
            <Button
              type="submit"
              className="betslip-wrapper__button btn--cta"
              text={ t('betslip.placeBet') }
              disabled={ processing }
              { ...(_.isEmpty(activeWallet) && !RETAIL_MODE && { link: '#login' }) }
            >
              {
                processing ? (
                  <span>{ t('betslip.placeBetBtn') }</span>
                ) : (
                  <>
                    {
                      (!activeWallet || activeWallet.typeCode !== 81 || _.isEmpty(activeWallet)) &&
                    <span>{ t('betslip.placeBetBtn', { value: processing ? '--.--' : totalAmount?.toFixed(2) }) }</span>
                    }
                    {
                      (activeWallet && activeWallet.typeCode === 81) &&
                      <div className="betslip-wrapper__bonus-container">
                        <span>Place</span>
                        <div className="betslip-wrapper__bonus-badge">Bonus Bet</div>
                        <span>{ processing ? '--.--' : totalAmount?.toFixed(2) }</span>
                      </div>
                    }
                    <span>
                      {
                        !_.some(bets, (bet) => (_.isNaN(+bet.odds) || !bet.odds)) &&
                          t(`betslip.${ slip === 1 && totalCombinations === 1 ? 'potentialReturnWithOdds' : 'potentialReturnBtn' }`, {
                            return: processing  ? '--.--' : maximumReturn?.toFixed(2),
                            odds: processing ? '--.--' : formatOdds(totalOdds, selectedOddsType)
                          })
                      }
                    </span>
                  </>
                )
              }
            </Button>
          )
        )
      }
    </>
  );
}

export default memo(SubmitButton);
